
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { safeClipboardWrite } from '../utils/safeStorage';
import { Submission, Participant } from '../types';
import { XIcon, ClipboardCopyIcon, CheckIcon, MailIcon, LockIcon, UnlockIcon, ShareIcon, ChevronDownIcon, EyeIcon, EyeOffIcon } from './icons';

interface ShareSubmissionModalProps {
  submission: Submission;
  participant?: Participant;
  onClose: () => void;
}

const ShareSubmissionModal: React.FC<ShareSubmissionModalProps> = ({ submission, participant, onClose }) => {
  const [activeTab, setActiveTab] = useState<'link' | 'email'>('link');
  const [isPasswordProtected, setIsPasswordProtected] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [expirationDays, setExpirationDays] = useState(0); // 0 for no expiration
  const [emails, setEmails] = useState('');
  const [copied, setCopied] = useState(false);
  const [invitesSent, setInvitesSent] = useState(false);
  const [isExpirationOpen, setIsExpirationOpen] = useState(false);
  const expirationRef = useRef<HTMLDivElement>(null);

  const shareLink = useMemo(() => {
    // In a real app, this would be generated by a backend service
    return `https://dekko.example.com/share/${submission.id}?token=${Math.random().toString(36).substring(2, 15)}`;
  }, [submission.id]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (expirationRef.current && !expirationRef.current.contains(event.target as Node)) {
        setIsExpirationOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCopyLink = () => {
    safeClipboardWrite(shareLink).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleSendInvites = () => {
    // Simulate sending invites
    console.log("Sending invites to:", emails.split(',').map(e => e.trim()));
    console.log("With settings:", { isPasswordProtected, password, expirationDays });
    setInvitesSent(true);
    setTimeout(() => {
        setInvitesSent(false);
        setEmails('');
    }, 3000);
  };
  
  const expirationOptions = [
    { value: 0, label: 'Never expires' },
    { value: 1, label: 'Expires in 1 day' },
    { value: 7, label: 'Expires in 7 days' },
    { value: 30, label: 'Expires in 30 days' },
  ];

  const selectedExpirationLabel = (expirationOptions.find(opt => opt.value === expirationDays) || { label: '' }).label;


  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 p-4 flex items-center justify-center" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-app-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
                <div className="bg-status-warn-bg text-status-warn p-2 rounded-full">
                    <ShareIcon className="w-6 h-6"/>
                </div>
                <div>
                    <h2 className="text-lg font-bold text-text-ink">Share Submission</h2>
                    <p className="text-sm text-text-muted">From {(participant && participant.name) || 'a participant'}</p>
                </div>
            </div>
            <button type="button" onClick={onClose} className="p-1 rounded-full text-text-muted hover:bg-neutral-100 active:bg-neutral-200">
              <XIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="flex p-1 bg-neutral-100 rounded-lg">
            <button
              onClick={() => setActiveTab('link')}
              className={`flex-1 py-2 text-sm font-semibold rounded-md transition-all active:scale-95 ${activeTab === 'link' ? 'bg-white shadow text-text-ink' : 'text-text-muted hover:bg-neutral-200'}`}
            >
              Share Link
            </button>
            <button
              onClick={() => setActiveTab('email')}
              className={`flex-1 py-2 text-sm font-semibold rounded-md transition-all active:scale-95 ${activeTab === 'email' ? 'bg-white shadow text-text-ink' : 'text-text-muted hover:bg-neutral-200'}`}
            >
              Invite by Email
            </button>
          </div>

          {activeTab === 'link' && (
            <div className="space-y-2 animate-fade-in-down">
              <p className="text-sm text-text-muted">Anyone with the link can view this submission, subject to the privacy controls below.</p>
              <div className="flex items-center gap-2">
                <input type="text" readOnly value={shareLink} className="w-full bg-neutral-100 border-transparent rounded-lg shadow-sm sm:text-sm text-text-muted focus:ring-2 focus:ring-action px-3 py-2" />
                <button
                  onClick={handleCopyLink}
                  className="flex-shrink-0 flex items-center gap-2 bg-white border border-app-border text-text-ink font-semibold py-2 px-3 rounded-lg hover:bg-neutral-100 active:bg-neutral-200 transition-colors text-sm"
                >
                  {copied ? <CheckIcon className="w-5 h-5 text-status-ok"/> : <ClipboardCopyIcon className="w-5 h-5" />}
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'email' && (
            <div className="space-y-4 animate-fade-in-down">
                <div>
                  <label htmlFor="share-emails" className="block text-xs font-medium text-text-muted mb-1">
                    Email addresses
                  </label>
                  <textarea
                    id="share-emails"
                    value={emails}
                    onChange={(e) => setEmails(e.target.value)}
                    placeholder="Enter emails, separated by commas"
                    rows={3}
                    className="w-full bg-white border border-app-border rounded-lg focus:outline-none focus:border-action focus:ring-1 focus:ring-action text-sm p-3"
                  />
                </div>
            </div>
          )}

          <div className="space-y-4 pt-4 border-t">
            <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider">Privacy Controls</h3>
             <div className="flex items-center justify-between p-3 bg-app-bg rounded-lg">
                <div className="flex items-center gap-3">
                    {isPasswordProtected ? <LockIcon className="w-5 h-5 text-text-muted"/> : <UnlockIcon className="w-5 h-5 text-text-muted"/>}
                    <label htmlFor="password-toggle" className="text-xs font-medium text-text-muted">
                        Require Password
                    </label>
                </div>
                <button
                    id="password-toggle"
                    onClick={() => setIsPasswordProtected(!isPasswordProtected)}
                    className={`relative inline-flex items-center h-6 rounded-full w-11 transition-all active:scale-90 ${isPasswordProtected ? 'bg-action' : 'bg-neutral-300'}`}
                >
                    <span className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${isPasswordProtected ? 'translate-x-6' : 'translate-x-1'}`}/>
                </button>
            </div>
            {isPasswordProtected && (
                 <div className="animate-fade-in-down">
                     <label htmlFor="share-password" className="block text-xs font-medium text-text-muted mb-1">Set Password</label>
                     <div className="relative">
                        <input 
                            type={showPassword ? "text" : "password"} 
                            id="share-password" 
                            value={password} 
                            onChange={e => setPassword(e.target.value)} 
                            className="w-full bg-white border border-app-border rounded-lg focus:outline-none focus:border-action focus:ring-1 focus:ring-action text-sm px-3 py-2 pr-10 h-10"
                        />
                         <button 
                            type="button" 
                            onClick={() => setShowPassword(!showPassword)} 
                            className="absolute inset-y-0 right-0 flex items-center pr-3 text-text-muted hover:text-text-ink active:text-text-ink"
                            aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                            {showPassword ? <EyeOffIcon className="w-5 h-5"/> : <EyeIcon className="w-5 h-5"/>}
                        </button>
                    </div>
                 </div>
            )}
            <div>
              <label htmlFor="expiration" className="block text-xs font-medium text-text-muted mb-1">
                Link Expiration
              </label>
               <div className="relative" ref={expirationRef}>
                <button
                    type="button"
                    id="expiration"
                    onClick={() => setIsExpirationOpen(!isExpirationOpen)}
                    className="h-10 w-full flex justify-between items-center text-left bg-neutral-100 border-transparent rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-action sm:text-sm active:bg-neutral-200 text-text-ink px-3 py-2"
                >
                    <span>{selectedExpirationLabel}</span>
                    <ChevronDownIcon className={`w-5 h-5 text-text-muted transition-transform ${isExpirationOpen ? 'rotate-180' : ''}`} />
                </button>
                {isExpirationOpen && (
                    <div className="absolute top-full mt-1 w-full bg-white rounded-md shadow-lg border border-app-border z-10">
                        <ul className="p-1">
                            {expirationOptions.map(option => (
                                <li key={option.value}>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setExpirationDays(option.value);
                                            setIsExpirationOpen(false);
                                        }}
                                        className={`w-full text-left px-3 py-1.5 rounded-md text-sm ${
                                            expirationDays === option.value
                                            ? 'bg-status-warn-bg text-status-warn'
                                            : 'text-text-ink hover:bg-neutral-100 active:bg-neutral-200'
                                        }`}
                                    >
                                        {option.label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 bg-app-bg rounded-b-2xl flex justify-end gap-3">
          <button type="button" onClick={onClose} className="bg-white border border-app-border text-text-ink font-semibold py-2 px-4 rounded-lg hover:bg-neutral-100 active:bg-neutral-200 transition-colors">
            Close
          </button>
          {activeTab === 'email' && (
             <button
                type="button"
                onClick={handleSendInvites}
                disabled={!emails.trim() || invitesSent}
                className="min-w-[140px] flex items-center justify-center gap-2 bg-action text-white font-semibold py-2 px-4 rounded-lg hover:bg-action-dark active:scale-95 transition-all disabled:bg-neutral-300 disabled:cursor-not-allowed"
              >
                {invitesSent ? <><CheckIcon className="w-5 h-5"/>Sent!</> : <><MailIcon className="w-5 h-5"/> Send Invites</>}
              </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareSubmissionModal;
